<template>
  <!-- class: a task upload prompts while the Edit Task slide-out is open, above Vuetify's dialog stack -->
  <v-dialog :value="active" max-width="760" persistent scrollable class="ds-dialog-above-slideout">
    <v-card>
      <v-card-title class="text-h6 font-weight-bold">
        {{ conflicts.length == 1 ? 'A file with this name already exists' : `${conflicts.length} files have names that already exist` }}
      </v-card-title>
      <v-card-subtitle class="pt-1">
        Choose for each file whether to upload it as a new version of the existing document, keep both
        under a different name, or skip it.
        <template v-if="identicalCount">
          {{ identicalCount == 1 ? '1 file is identical to its current version and is' : `${identicalCount} files are identical to their current versions and are` }}
          set to be skipped.
        </template>
      </v-card-subtitle>
      <v-divider></v-divider>

      <div v-if="conflicts.length > 1" class="d-flex align-center px-6 pt-3" style="gap: 8px">
        <span class="text-caption text--secondary mr-1">Apply to all:</span>
        <v-btn x-small outlined :disabled="!anyVersionable" @click="setAll(ACTIONS.VERSION)">New version</v-btn>
        <v-btn x-small outlined @click="setAll(ACTIONS.RENAME)">Keep both</v-btn>
        <v-btn x-small outlined @click="setAll(ACTIONS.SKIP)">Skip</v-btn>
        <template v-if="anyAsksAboutLinks">
          <span class="text-caption text--secondary ml-3 mr-1">Links:</span>
          <v-btn x-small outlined @click="setAllLinks(true)">Transfer</v-btn>
          <v-btn x-small outlined @click="setAllLinks(false)">Don't transfer</v-btn>
        </template>
      </div>

      <v-card-text class="pt-3" style="max-height: 60vh">
        <div v-for="conflict in conflicts" :key="conflict.index" class="name-conflict-row py-3">
          <div class="d-flex align-center" style="gap: 8px">
            <span style="font-size: 22px" :class="`fiv-viv fiv-icon-${$fileExtension(fileNameOf(conflict))}`"></span>
            <div style="min-width: 0">
              <div class="font-weight-medium text-overflow" :title="fileNameOf(conflict)">{{ fileNameOf(conflict) }}</div>
              <div class="text-caption text--secondary">
                {{ describe(conflict) }}
                <span v-if="comparingIndex === conflict.index" class="name-conflict-comparing">
                  <v-progress-circular indeterminate size="10" width="1.5" class="mr-1" />Comparing with the current version…
                </span>
              </div>
            </div>
          </div>

          <IdenticalFileNotice
            v-if="identical[conflict.index]"
            class="mt-2 ml-8"
            :version-number="conflict.currentVersionNumber"
            :file-name="conflict.existingFileName"
            :size="conflict.currentSize"
            :content-hash="conflict.currentContentHash"
            hint="It is set to be skipped. Upload it anyway only if you need a copy as its own version." />

          <v-radio-group
            v-model="choices[conflict.index].action"
            dense
            hide-details
            class="mt-2 ml-8"
            @change="touched[conflict.index] = true">
            <v-radio
              :value="ACTIONS.VERSION"
              :disabled="!conflict.existingDocumentId"
              :label="versionLabel(conflict)"></v-radio>
            <v-radio :value="ACTIONS.RENAME" label="Keep both, upload as a new document named:"></v-radio>
            <v-text-field
              v-if="choices[conflict.index].action == ACTIONS.RENAME"
              v-model="choices[conflict.index].documentNumber"
              class="ml-8 mb-1"
              dense
              outlined
              hide-details="auto"
              :rules="[(v) => !!(v || '').trim() || 'A name is required']"
              @input="(v) => (choices[conflict.index].documentTitle = v)"></v-text-field>
            <v-radio :value="ACTIONS.SKIP" label="Skip this file"></v-radio>
          </v-radio-group>

          <!-- Links belong to one version: a new version of a document with links asks about them. -->
          <div v-if="asksAboutLinks(choices[conflict.index])" class="name-conflict-links ml-16 mt-1">
            <div class="text-caption" :class="choices[conflict.index].transferLinks === null ? 'warning--text' : 'text--secondary'">
              {{ choices[conflict.index].transferLinks === null ? 'Choose whether to transfer the links:' : 'Links:' }}
            </div>
            <v-radio-group v-model="choices[conflict.index].transferLinks" dense hide-details class="mt-0 pt-0">
              <v-radio :value="true" :label="transferLinksLabel(conflict)"></v-radio>
              <v-radio :value="false" label="Start the new version without links"></v-radio>
            </v-radio-group>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn text color="red" @click="finish(null)">Cancel upload</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" elevation="0" :disabled="!valid || comparing" :loading="comparing" @click="finish(choices)">
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { EventBus, DOCUMENT_EVENTS } from '@/event-bus';
  import { error as debugError } from '@/utils/debugLogger';
  import IdenticalFileNotice from './IdenticalFileNotice.vue';
  import { hasSameContent } from '../utils/documentVersions';
  import {
    NAME_CONFLICT_ACTIONS,
    defaultChoice,
    isChoiceValid,
    describeConflict,
    newVersionLabel,
    asksAboutLinks,
    transferLinksLabel,
  } from '../utils/documentNameConflicts';

  /**
   * App-wide prompt for uploads whose names are already taken in the facility or facility group.
   * Driven by resolveDocumentNameConflicts() through DOCUMENT_EVENTS.RESOLVE_NAME_CONFLICTS.
   * When the request hands over the files (`fileOf`), each file offered as a new version is
   * compared with the existing document's current version; an identical one is flagged and set to
   * be skipped, unless the user already picked an option for it.
   */
  export default {
    name: 'DocumentNameConflictsDialog',
    components: { IdenticalFileNotice },
    data() {
      return {
        ACTIONS: NAME_CONFLICT_ACTIONS,
        active: false,
        conflicts: [],
        drafts: [],
        scopeLabel: 'this facility',
        choices: {},
        resolve: null,
        queue: [],
        fileOf: null,
        // By conflict index: the file has the current version's bytes / the user picked an option.
        identical: {},
        touched: {},
        comparing: false,
        comparingIndex: null,
        // Bumped per prompt so a comparison still running for a closed prompt is ignored.
        compareRun: 0,
      };
    },
    computed: {
      valid() {
        return this.conflicts.every((c) => isChoiceValid(this.choices[c.index]));
      },
      anyVersionable() {
        return this.conflicts.some((c) => c.existingDocumentId);
      },
      identicalCount() {
        return Object.keys(this.identical).length;
      },
      anyAsksAboutLinks() {
        return this.conflicts.some((c) => asksAboutLinks(this.choices[c.index]));
      },
    },
    created() {
      EventBus.$on(DOCUMENT_EVENTS.RESOLVE_NAME_CONFLICTS, this.onRequest);
    },
    beforeDestroy() {
      EventBus.$off(DOCUMENT_EVENTS.RESOLVE_NAME_CONFLICTS, this.onRequest);
      // Waiting uploads are cancelled, not left hanging.
      this.queue.forEach((request) => request.resolve(null));
      this.queue = [];
      this.finish(null);
    },
    methods: {
      onRequest(request) {
        // One prompt at a time; a second upload waits for the first answer.
        if (this.active) this.queue.push(request);
        else this.show(request);
      },
      show({ conflicts, drafts, scopeLabel, fileOf, resolve }) {
        this.conflicts = conflicts || [];
        this.drafts = drafts || [];
        this.scopeLabel = scopeLabel || 'this facility';
        const choices = {};
        this.conflicts.forEach((c) => (choices[c.index] = defaultChoice(c)));
        this.choices = choices;
        this.fileOf = typeof fileOf === 'function' ? fileOf : null;
        this.identical = {};
        this.touched = {};
        this.resolve = resolve;
        this.active = true;
        this.compareContents();
      },
      /**
       * One file at a time: hashing reads the whole file into memory. Only files of the current
       * version's size are hashed. A failed comparison flags nothing; the API still drops a copy.
       */
      async compareContents() {
        const run = ++this.compareRun;
        const candidates = this.fileOf ? this.conflicts.filter((c) => c.existingDocumentId && c.currentContentHash) : [];
        this.comparing = candidates.length > 0;
        for (const conflict of candidates) {
          if (run !== this.compareRun) return;
          const file = this.fileOf(this.drafts[conflict.index]);
          if (!file || Number(conflict.currentSize) !== file.size) continue;
          this.comparingIndex = conflict.index;
          let same = false;
          try {
            same = await hasSameContent(file, conflict.currentSize, conflict.currentContentHash);
          } catch (err) {
            debugError('[DocumentNameConflictsDialog]', 'Could not compare with the current version:', err);
          }
          if (run !== this.compareRun) return;
          if (same) {
            this.$set(this.identical, conflict.index, true);
            const choice = this.choices[conflict.index];
            if (!this.touched[conflict.index] && choice.action === NAME_CONFLICT_ACTIONS.VERSION) {
              choice.action = NAME_CONFLICT_ACTIONS.SKIP;
            }
          }
        }
        if (run !== this.compareRun) return;
        this.comparingIndex = null;
        this.comparing = false;
      },
      finish(choices) {
        const resolve = this.resolve;
        this.resolve = null;
        this.active = false;
        this.compareRun++;
        this.comparing = false;
        this.comparingIndex = null;
        let answer = null;
        if (choices) {
          answer = JSON.parse(JSON.stringify(choices));
          // A new version kept although identical to the current one: the API must add it anyway.
          Object.keys(answer).forEach((index) => (answer[index].identical = !!this.identical[index]));
        }
        this.fileOf = null;
        if (resolve) resolve(answer);
        const next = this.queue.shift();
        if (next) this.$nextTick(() => this.show(next));
      },
      setAll(action) {
        this.conflicts.forEach((c) => {
          if (action === NAME_CONFLICT_ACTIONS.VERSION && !c.existingDocumentId) return;
          // A copy of the current version is uploaded only when picked on its own row.
          if (action === NAME_CONFLICT_ACTIONS.VERSION && this.identical[c.index]) return;
          this.choices[c.index].action = action;
          this.touched[c.index] = true;
        });
      },
      /** Answers "transfer the links?" for every file added as a new version of a document with links. */
      setAllLinks(transfer) {
        this.conflicts.forEach((c) => {
          const choice = this.choices[c.index];
          if (asksAboutLinks(choice)) choice.transferLinks = transfer;
        });
      },
      asksAboutLinks,
      transferLinksLabel,
      fileNameOf(conflict) {
        const draft = this.drafts[conflict.index] || {};
        return draft.fileName || draft.documentNumber || '';
      },
      describe(conflict) {
        return describeConflict(conflict, this.scopeLabel);
      },
      versionLabel(conflict) {
        if (!conflict.existingDocumentId) return 'Upload as a new version (no existing document)';
        return newVersionLabel(conflict, !!this.identical[conflict.index]);
      },
    },
  };
</script>

<style>
  .name-conflict-row + .name-conflict-row {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
  .name-conflict-comparing {
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
  }
</style>
